import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { theme } from '../styles/GlobalStyles';
import { HeaderWaves } from '../components/WaveShapes';
import { UserIcon, CalendarIcon, TargetIcon, BotIcon, ShoppingCartIcon, GlobeIcon } from '../components/Icons';

const StorefrontContainer = styled.div`
  min-height: 100vh;
  background-color: ${theme.colors.white};
  position: relative;
  overflow-x: hidden;
`;

const Header = styled.header`
  position: relative;
  background: #ffffff;
  padding: ${theme.spacing.lg} 0;
  z-index: 10;
  border-bottom: 1px solid #e9ecef;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.md} 0;
  }
`;

const WaveBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  height: 100%;
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.lg};
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 10;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0 ${theme.spacing.md};
    flex-wrap: wrap;
    gap: ${theme.spacing.sm};
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  min-height: 50px;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    min-height: 40px;
  }
`;

const LogoImage = styled.img`
  height: 50px;
  width: auto;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    height: 40px;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: ${theme.spacing.sm};
  }
`;

const CurrencySelector = styled.select`
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 50%;
  color: #2c3e50;
  padding: ${theme.spacing.sm};
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  appearance: none;
  
  &:hover {
    border-color: ${theme.colors.primary};
    background: #f8f9fa;
  }
  
  option {
    background: #ffffff;
    color: #2c3e50;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 36px;
    height: 36px;
    padding: ${theme.spacing.xs};
  }
`;

const CurrencyButton = styled.button`
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 50%;
  color: #2c3e50;
  padding: 0;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  font-size: 0.7rem;
  font-weight: 600;
  line-height: 1;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  letter-spacing: 0.5px;
  
  &:hover {
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
    background: #f8f9fa;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 36px;
    height: 36px;
    font-size: 0.6rem;
  }
`;

const CurrencyDropdown = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 9999;
  min-width: 120px;
  margin-top: 4px;
  display: ${props => props.isOpen ? 'block' : 'none'};
`;

const CurrencyOption = styled.button<{ isSelected: boolean }>`
  width: 100%;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: none;
  background: ${props => props.isSelected ? '#f8f9fa' : '#ffffff'};
  color: #2c3e50;
  text-align: left;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: #f8f9fa;
  }
  
  &:first-child {
    border-radius: 8px 8px 0 0;
  }
  
  &:last-child {
    border-radius: 0 0 8px 8px;
  }
  
  &:only-child {
    border-radius: 8px;
  }
`;

const HeaderButton = styled.button`
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 50%;
  color: #2c3e50;
  padding: ${theme.spacing.sm};
  cursor: pointer;
  transition: all 0.2s ease;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  &:hover {
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
    background: #f8f9fa;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 36px;
    height: 36px;
    padding: ${theme.spacing.xs};
  }
`;

const CartBadge = styled.span`
  background: ${theme.colors.secondary};
  color: white;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  font-size: 0.6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: -4px;
  right: -4px;
  font-weight: 600;
`;

const HeroSection = styled.section`
  position: relative;
  padding: ${theme.spacing.xxl} 0;
  text-align: center;
  background: #ffffff;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.xl} 0;
  }
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.lg};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0 ${theme.spacing.md};
  }
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  color: ${theme.colors.deepDark};
  margin-bottom: ${theme.spacing.lg};
  line-height: 1.2;
  font-weight: 600;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 2.2rem;
    margin-bottom: ${theme.spacing.md};
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: ${theme.colors.lightText};
  margin-bottom: ${theme.spacing.xl};
  line-height: 1.6;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1rem;
    margin-bottom: ${theme.spacing.lg};
  }
`;

const CTAButton = styled.button`
  background: linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.tertiary});
  border: none;
  border-radius: 8px;
  color: white;
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.md} ${theme.spacing.lg};
    font-size: 1rem;
    width: 100%;
    max-width: 300px;
  }
`;

const FeaturesSection = styled.section`
  padding: ${theme.spacing.xxl} 0;
  background-color: ${theme.colors.background};
`;

const FeaturesContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.lg};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0 ${theme.spacing.md};
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${theme.spacing.lg};
  margin-top: ${theme.spacing.xl};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.md};
  }
`;

const FeatureCard = styled.div`
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: ${theme.spacing.xl};
  text-align: center;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${theme.colors.primary};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.lg};
  }
`;

const FeatureIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.tertiary});
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${theme.spacing.md};
  color: white;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 50px;
    height: 50px;
    margin-bottom: ${theme.spacing.sm};
  }
`;

const FeatureTitle = styled.h3`
  color: ${theme.colors.deepDark};
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: ${theme.spacing.sm};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.1rem;
  }
`;

const FeatureDescription = styled.p`
  color: ${theme.colors.lightText};
  line-height: 1.6;
  font-size: 0.9rem;
  margin: 0;
`;

const SectionTitle = styled.h2`
  text-align: center;
  color: ${theme.colors.deepDark};
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: ${theme.spacing.lg};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.6rem;
    margin-bottom: ${theme.spacing.md};
  }
`;

const Footer = styled.footer`
  background: ${theme.colors.darkAccent};
  color: #ffffff;
  padding: ${theme.spacing.lg} 0;
  text-align: center;
  font-size: 0.9rem;
`;

const Storefront: React.FC = () => {
  const [currency, setCurrency] = useState('USD');
  const [isCurrencyDropdownOpen, setIsCurrencyDropdownOpen] = useState(false);
  const navigate = useNavigate();

  const currencies = [
    { code: 'USD', flag: '🇺🇸', name: 'USD', country: 'US' },
    { code: 'EUR', flag: '🇪🇺', name: 'EUR', country: 'EU' },
    { code: 'GBP', flag: '🇬🇧', name: 'GBP', country: 'GB' },
    { code: 'CAD', flag: '🇨🇦', name: 'CAD', country: 'CA' },
    { code: 'AUD', flag: '🇦🇺', name: 'AUD', country: 'AU' }
  ];

  const handleLoginClick = () => {
    navigate('/login');
  };

  const handleGetStartedClick = () => {
    navigate('/login');
  };

  const handleCurrencyClick = () => {
    setIsCurrencyDropdownOpen(!isCurrencyDropdownOpen);
  };

  const handleCurrencySelect = (currencyCode: string) => {
    setCurrency(currencyCode);
    setIsCurrencyDropdownOpen(false);
  };

  const getCurrentCurrency = () => {
    return currencies.find(curr => curr.code === currency) || currencies[0];
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isCurrencyDropdownOpen) {
        setIsCurrencyDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isCurrencyDropdownOpen]);

  return (
    <StorefrontContainer>
      <Header>
        <WaveBackground>
          <HeaderWaves />
        </WaveBackground>
        <HeaderContent>
          <Logo>
            <LogoImage src="/images/MMNHeader.png" alt="NeuroNest" />
          </Logo>
          
          <HeaderActions>
            <div style={{ position: 'relative' }}>
              <CurrencyButton 
                title="Change Currency"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCurrencyClick();
                }}
              >
                {getCurrentCurrency().country}
              </CurrencyButton>
              <CurrencyDropdown isOpen={isCurrencyDropdownOpen}>
                {currencies.map((curr) => (
                  <CurrencyOption
                    key={curr.code}
                    isSelected={currency === curr.code}
                    onClick={() => handleCurrencySelect(curr.code)}
                  >
                    {curr.flag} {curr.name}
                  </CurrencyOption>
                ))}
              </CurrencyDropdown>
            </div>
            
            <HeaderButton onClick={handleLoginClick} title="Login">
              <UserIcon size={16} />
            </HeaderButton>
            
            <HeaderButton title="Shopping Cart">
              <ShoppingCartIcon size={16} />
              <CartBadge>3</CartBadge>
            </HeaderButton>
          </HeaderActions>
        </HeaderContent>
      </Header>

      <HeroSection>
        <HeroContent>
          <HeroTitle>Transform Your Productivity Journey</HeroTitle>
          <HeroSubtitle>
            Discover powerful tools and resources designed for busy minds. 
            Plan, organize, and thrive with our comprehensive productivity management platform.
          </HeroSubtitle>
          <CTAButton onClick={handleGetStartedClick}>
            Get Started Today
          </CTAButton>
        </HeroContent>
      </HeroSection>

      <FeaturesSection>
        <FeaturesContainer>
          <SectionTitle>Why Choose NeuroNest?</SectionTitle>
          <FeaturesGrid>
            <FeatureCard>
              <FeatureIcon>
                <CalendarIcon size={28} />
              </FeatureIcon>
              <FeatureTitle>Smart Planning</FeatureTitle>
              <FeatureDescription>
                AI-powered planning tools that adapt to your unique thinking patterns and help you stay organized effortlessly.
              </FeatureDescription>
            </FeatureCard>
            
            <FeatureCard>
              <FeatureIcon>
                <TargetIcon size={28} />
              </FeatureIcon>
              <FeatureTitle>Focus Enhancement</FeatureTitle>
              <FeatureDescription>
                Specialized techniques and tools designed to improve focus and reduce distractions for better productivity.
              </FeatureDescription>
            </FeatureCard>
            
            <FeatureCard>
              <FeatureIcon>
                <BotIcon size={28} />
              </FeatureIcon>
              <FeatureTitle>AI-Powered Assistance</FeatureTitle>
              <FeatureDescription>
                Built for busy minds, with AI assistance that understands your unique thinking style and workflow preferences.
              </FeatureDescription>
            </FeatureCard>
          </FeaturesGrid>
        </FeaturesContainer>
      </FeaturesSection>

      <Footer>
        <p>&copy; 2025 My NeuroNest. All rights reserved. Empowering busy minds.</p>
      </Footer>
    </StorefrontContainer>
  );
};

export default Storefront; 